import { matchSorter } from "match-sorter";
import {
  uncategorisedKey,
  type BudgetResult,
  type BudgetRowKey,
  type IsoMonth,
  type Snapshot,
} from "@budgeter/shared";
import { categoryGroups } from "../../data/selectors";
import { balanceTone, type BalanceTone } from "../../ui";

export interface HomeRow {
  key: BudgetRowKey;
  name: string;
  available: number;
  tone: BalanceTone;
  /** Negative in a reset category: counts as overspent (SPEC §5.2). Carry categories don't. */
  overspent: boolean;
}

export interface HomeGroup {
  id: string;
  name: string;
  rows: HomeRow[];
}

export interface HomeFilter {
  query?: string;
  overspentOnly?: boolean;
}

/**
 * Home's category balances for `month`, grouped by master category, with Uncategorised pinned
 * under "Needs attention" when it isn't zero. Callers pass the current month only: earlier
 * months are excluded from overspent warnings (SPEC §5.2).
 */
export function homeGroups(
  s: Snapshot,
  budget: BudgetResult,
  month: IsoMonth,
  filter: HomeFilter = {},
): { groups: HomeGroup[]; overspentCount: number } {
  const all: HomeGroup[] = [];
  const unc = budget.uncategorised(month);
  if (unc.available !== 0 || unc.activity !== 0) {
    all.push({
      id: "attention",
      name: "Needs attention",
      rows: [
        {
          key: uncategorisedKey,
          name: "Uncategorised",
          available: unc.available,
          tone: balanceTone(unc.available, unc.budgeted, false),
          overspent: unc.available < 0,
        },
      ],
    });
  }
  for (const g of categoryGroups(s)) {
    all.push({
      id: g.master.id,
      name: g.master.name,
      rows: g.categories.map((c) => {
        const cell = budget.cell(c.id, month);
        const carry = c.overspend === "carry";
        return {
          key: c.id,
          name: c.name,
          available: cell.available,
          tone: balanceTone(cell.available, cell.budgeted, carry),
          overspent: cell.available < 0 && !carry,
        };
      }),
    });
  }
  const overspentCount = all.reduce((n, g) => n + g.rows.filter((r) => r.overspent).length, 0);

  const q = filter.query?.trim() ?? "";
  const matching = q
    ? new Set(
        matchSorter(
          all.flatMap((g) => g.rows),
          q,
          { keys: ["name"] },
        ).map((r) => r.key),
      )
    : null;
  const groups = all
    .map((g) => ({
      ...g,
      rows: g.rows.filter(
        (r) => (!filter.overspentOnly || r.overspent) && (!matching || matching.has(r.key)),
      ),
    }))
    .filter((g) => g.rows.length > 0);
  return { groups, overspentCount };
}
